import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { Input } from '@platform/ui'

import { SEARCH_INPUT_PARAM } from '@/hooks/usePreselectFilters'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { setCatalogCoursesSearchText, setEnrolledCoursesSearchText } from '@/store/slices/aggregatesSlice'
import styles from './CourseSearch.module.scss'

type CourseSearchProps = {
  type: 'enrolled' | 'catalog'
}

export const CourseSearch: FC<CourseSearchProps> = ({ type }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const timeoutId = useRef<ReturnType<typeof setTimeout>>()

  const [inputText, setInputText] = useState('')
  const location = useLocation()
  const navigate = useNavigate()
  const prefillInputHandled = useRef(false)

  const storeText = useAppSelector((state) =>
    type === 'enrolled' ? state.aggregates.enrolledCoursesSearchText : state.aggregates.catalogCoursesSearchText,
  )

  const syncTextWithUrl = useCallback(
    (text: string) => {
      const searchParams = new URLSearchParams(location.search)

      if (text) {
        searchParams.set(SEARCH_INPUT_PARAM, text)
      } else {
        searchParams.delete(SEARCH_INPUT_PARAM)
      }

      navigate({ search: searchParams.toString() }, { replace: true })
    },
    [location.search, navigate],
  )

  const handleChange = useCallback(
    (inputValue: string, syncUrl = true) => {
      const filtered = inputValue?.toLocaleLowerCase()?.trimStart()

      clearTimeout(timeoutId.current!)

      if (type === 'catalog') {
        timeoutId.current = setTimeout(() => dispatch(setCatalogCoursesSearchText(filtered)), 500)
      } else {
        timeoutId.current = setTimeout(() => dispatch(setEnrolledCoursesSearchText(filtered)), 500)
      }

      setInputText(filtered)

      if (syncUrl) {
        syncTextWithUrl(filtered)
      }
    },
    [dispatch, syncTextWithUrl, type],
  )

  useEffect(() => {
    if (prefillInputHandled.current) return

    const params = new URLSearchParams(location.search)
    const search = params.get(SEARCH_INPUT_PARAM)

    handleChange(search ?? storeText, false)

    prefillInputHandled.current = true
  }, [handleChange, location.search, storeText])

  return (
    <div className={styles.catalogSearch}>
      <Input
        key={t('catalogList.search')}
        iconBeforeText="Search"
        size="small"
        placeholder={t('catalogList.search')}
        title={t('catalogList.search')}
        aria-label={t('catalogList.search')}
        onChange={(value) => handleChange(value)}
        value={inputText}
      />
    </div>
  )
}
